import React, { FormEventHandler, useState } from "react";

import { Button } from "components/Button";
import { EMAIL_TEMPLATE_IDS, sendEmail } from "helpers/email";
import { BodyText, LightSecondaryText } from "styles";

import * as Styles from "./ContactForm.styles";
import { FormLabel } from "./FormLabel";
import { Input } from "./Input";
import { TextArea } from "./TextArea";

const DEFAULT_FORM_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  companyName: "",
  jobTitle: "",
  message: "",
};

const isDataValid = (data: typeof DEFAULT_FORM_VALUES) => {
  const { phone, ...requiredData } = data;

  return Object.values(requiredData).every(Boolean);
};

export const ContactForm = () => {
  const [values, setValues] = useState(DEFAULT_FORM_VALUES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const updateValue = (key: keyof typeof values, value: string) => {
    setValues((values) => ({ ...values, [key]: value }));
  };

  const onSubmit: FormEventHandler = async (event) => {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      setIsSubmitted(false);

      await sendEmail({
        templateId: EMAIL_TEMPLATE_IDS.contact,
        params: values,
      });

      setIsSubmitted(true);
    } finally {
      setValues(DEFAULT_FORM_VALUES);
      setIsSubmitting(false);
    }
  };

  return (
    <Styles.Form className="xl:px-14 xl:pb-12 xl:pt-14" onSubmit={onSubmit}>
      <div className="grid grid-cols-1 gap-x-11 gap-y-10 md:grid-cols-2">
        <FormLabel>
          First name
          <Input
            type="text"
            placeholder="Enter first name"
            required
            onChange={(event) => updateValue("firstName", event.target.value)}
            value={values.firstName}
          />
        </FormLabel>

        <FormLabel>
          Last name
          <Input
            type="text"
            placeholder="Enter last name"
            required
            onChange={(event) => updateValue("lastName", event.target.value)}
            value={values.lastName}
          />
        </FormLabel>

        <FormLabel>
          Email address
          <Input
            type="email"
            placeholder="Enter email address"
            required
            onChange={(event) => updateValue("email", event.target.value)}
            value={values.email}
          />
        </FormLabel>

        <FormLabel>
          <span>
            Phone number <LightSecondaryText>(optional)</LightSecondaryText>
          </span>
          <Input
            type="text"
            placeholder="Enter phone number"
            onChange={(event) => updateValue("phone", event.target.value)}
            value={values.phone}
          />
        </FormLabel>

        <FormLabel>
          Company name
          <Input
            type="text"
            placeholder="Enter company name"
            required
            onChange={(event) => updateValue("companyName", event.target.value)}
            value={values.companyName}
          />
        </FormLabel>

        <FormLabel>
          Job title
          <Input
            type="text"
            placeholder="Enter job title"
            required
            onChange={(event) => updateValue("jobTitle", event.target.value)}
            value={values.jobTitle}
          />
        </FormLabel>

        <div className="md:col-span-2">
          <FormLabel>
            Message
            <TextArea
              placeholder="Enter your message"
              rows={8}
              required
              onChange={(event) => updateValue("message", event.target.value)}
              value={values.message}
            />
          </FormLabel>
        </div>
      </div>

      {isSubmitted && (
        <BodyText $variant="b4bold" className="pb-0 pt-8">
          Thank you for contacting us. We’ll aim to respond to your message
          within 48 hours.
        </BodyText>
      )}

      <div className="flex justify-end pt-7">
        <Button disabled={isSubmitting || !isDataValid(values)}>
          Send message
        </Button>
      </div>
    </Styles.Form>
  );
};
